/**
 * NOTE: integrationCatalog is config values for integrations which have code that lives in Pendo's backend
 * this catalog listing is merged with the extension listing that lives remote in pendo-extensions repo
 *
 * to add a new card to the integrations page, include an object in the following format:
 * {
 *     name: 'slack'// this is essentially an ID of sorts. make it unique, JS type
 *     label: 'Slack' // provider name, used for card title. "Why not just use a filter?"... it's just easier this way.
 *     author: {}, // copy from other entry
 *     private: false,
 *     tags: ['NPS'], // tags here is an array, automatically used to group integrations by like tags
 *     media: {
 *         default: require('@img/integrations/slack.svg'), // integration card logo - svg should be 80x80
 *         thumb: require('@img/integrations/slack_thumbnail.svg'), // used in the integration-info component, 40x40
 *         button: require('@img/integrations/slack_button.svg'), // integration-preview-modal action button at bottom right
 *         previews: ['https://blog.pendo.io/wp-content/uploads/2018/02/subscriptionSettingsiOSHover.png'] // screenshots of integration
 *     },
 *     brandColor: '#FFFFFF', // used for card background color, should match whatever brand guidelines the third party has
 *     description: 'Pendo’s NPS for Slack allows you to see real-time ... ', // short desc, used in the integration-info component
 *     disclaimer:
            ['While we want to make it easy for you to share NPS responses...', ''Selecting a private Slack channel...'] //array of disclaimers for the integration
 *     helpUrl: 'https://support.pendo.io/hc/en-us/articles/360032209591-Slack-Integration-to-Push-NPS-Responses', // support.pendo.io article. leave blank if no article available
 *     options: [],
 *     requiredFlags: ['enableSlackUnfurling'], // hide integration tile behind subscription flags
 *     addButtonLabel: 'Start using Slack' // optional override for the OAuth "Add <displayName>" action button label
 *  }
 */

import { PENDO_INTERNAL_SUB, ENTITY_TYPE_INTEGRATION, ENTITY_TYPE_SUBSCRIPTION } from './integration-constants';
import { i18n_t as $t } from '@/utils/i18n';

export const getIntegrationCatalog = () => [
    {
        name: 'microsoft-teams-send-message',
        provider: 'microsoft-teams',
        label: $t('teamsSetup.send_messages.catalog_label'),
        displayName: $t('teamsSetup.send_messages.catalog_label'),
        addButtonLabel: $t('teamsSetup.send_messages.catalog_add_button_label'),
        previewable: true,
        categories: ['Data Destination'],
        dataDirection: 'Data Out',
        types: ['Native'],
        author: {
            name: 'Pendo',
            email: '<admin>@pendo.io',
            url: 'https://www.pendo.io',
        },
        oauth: {
            uriConfig: {
                category: 'send-message',
                entityType: ENTITY_TYPE_SUBSCRIPTION,
                provider: 'microsoft-teams',
            },
        },
        free: true,
        private: false,
        supportsOauth: true,
        isBeta: true,
        oauthType: 'standard',
        tags: ['NPS', 'Data Destination'],
        media: {},
        /* eslint-disable internal-rules/no-hardcoded-colors */
        brandColor: '#6264A7',
        /* eslint-enable internal-rules/no-hardcoded-colors */
        description: $t('teamsSetup.send_messages.catalog_description'),
        marketingUrl: '',
        helpUrl: '',
        hideEditButton: true,
        ui: {},
        options: [],
    },
    {
        name: 'microsoft-teams-agent',
        provider: 'microsoft-teams',
        label: 'Pendo Agent for Microsoft Teams',
        displayName: 'Pendo Agent for Microsoft Teams',
        categories: ['Collaboration'],
        dataDirection: 'Two-Way',
        types: ['Native'],
        author: {
            name: 'Pendo',
            email: '<admin>@pendo.io',
            url: 'https://www.pendo.io',
        },
        free: true,
        private: false,
        oauth: {
            uriConfig: {
                category: 'agent',
                entityType: ENTITY_TYPE_SUBSCRIPTION,
                provider: 'microsoft-teams',
            },
        },
        supportsOauth: true,
        isBeta: true,
        oauthType: 'standard',
        tags: ['Collaboration', 'AI'],
        media: {},
        /* eslint-disable internal-rules/no-hardcoded-colors */
        brandColor: '#6264A7',
        /* eslint-enable internal-rules/no-hardcoded-colors */
        description:
            'Ask questions about your product data directly from Microsoft Teams. Get answers and summaries, and take action without leaving your conversation.',
        marketingUrl: '',
        helpUrl: '',
        options: [],
        hideEditButton: true,
        ui: {},
        requirements: ['Leo'],
        requiredFlags: ['askPendo'],
    },
    {
        name: 'slack',
        provider: 'Slack',
        label: 'Slack',
        previewable: true,
        categories: ['Collaboration'],
        displayName: 'Slack NPS',
        dataDirection: 'Data Out',
        types: ['Native'],
        author: {
            name: 'Pendo',
            email: '<admin>@pendo.io',
            url: 'https://www.pendo.io',
        },
        free: true,
        private: false,
        oauth: {
            uriConfig: {
                category: 'nps',
                // entityId will be derived automatically based on the entityType
                // (if Subscription, current sub ID, if Integration, use provider value)
                entityType: ENTITY_TYPE_INTEGRATION,
                provider: 'slack',
            },
        },
        supportsOauth: true,
        oauthType: 'standard',
        tags: ['NPS', 'Collaboration'],
        media: {
            default: require('@img/integrations/slack.svg'),
            provider: require('@img/integrations/Slack_Logo.svg'),
            thumb: require('@img/integrations/slack.svg'),
            icon: require('@img/integrations/Icon_List_Slack.svg'),
            screenshots: [],
        },
        /* eslint-disable internal-rules/no-hardcoded-colors */
        brandColor: '#FFFFFF',
        /* eslint-enable internal-rules/no-hardcoded-colors */
        description:
            'Push a preview of product analytics, guides, and NPS information into your instance of Slack. Create a channel specifically for NPS or see a data rich preview when you paste any Pendo link in Slack.',
        disclaimer: [
            'Selecting a private Slack channel for your NPS integration requires that you **/invite @Pendo** from that channel after installing the Slack app.',
            'You are electing to share NPS information, which may include personal information, with all members of your selected Slack channel.',
        ],
        marketingUrl: 'https://www.pendo.io/integrations/slack/',
        helpUrl: 'https://support.pendo.io/hc/en-us/articles/360032209591-Slack-Integration-to-Push-NPS-Responses',
        ui: {
            type: 'integration-tabs',
            tabs: [
                {
                    title: 'Configuration',
                    type: 'formly-form',
                    active: true,
                    form: {
                        options: {
                            formstate: {},
                        },
                        model: 'this.integration.config',
                        fields: [
                            {
                                key: 'featureFlags',
                                wrapper: 'generalOptions',
                                className: 'grouped-options-wrapper',
                                fieldGroup: [
                                    {
                                        wrapper: 'groupedOptions',
                                        className: 'subgrouped-options-wrapper',
                                        templateOptions: {
                                            label: 'Notifications',
                                        },
                                        fieldGroup: [
                                            {
                                                key: 'npsMessaging',
                                                type: 'toggleSwitch',
                                                defaultValue: true,
                                                templateOptions: {
                                                    group: 'general',
                                                    subgroup: 'notifications',
                                                    required: false,
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                key: 'messageSchema',
                                wrapper: 'generalOptions',
                                className: 'grouped-options-wrapper',
                                fieldGroup: [
                                    {
                                        wrapper: 'groupedOptions',
                                        className: 'subgrouped-options-wrapper',
                                        templateOptions: {
                                            label: 'Visitor Metadata',
                                        },
                                        fieldGroup: [
                                            {
                                                key: 'visitor',
                                                type: 'multiInput',
                                                defaultValue: [],
                                                templateOptions: {
                                                    inputOptions: {
                                                        type: 'uiSelect',
                                                        templateOptions: {
                                                            required: true,
                                                            optionsAttr: 'bs-options',
                                                            valueProp: 'value',
                                                            labelProp: 'label',
                                                            groupProp: 'group',
                                                            placeholder: {
                                                                select: 'Select Visitor Metadata',
                                                                search: 'Search Visitor Metadata',
                                                            },
                                                            options: [],
                                                        },
                                                    },
                                                },
                                                data: {
                                                    metadataKind: 'visitor',
                                                    addButtonText: '+ Add Visitor Metadata',
                                                    immutableFields: [
                                                        {
                                                            name: 'id',
                                                            label: 'Visitor ID',
                                                        },
                                                    ],
                                                },
                                            },
                                        ],
                                    },
                                    {
                                        wrapper: 'groupedOptions',
                                        className: 'subgrouped-options-wrapper',
                                        templateOptions: {
                                            label: 'Account Metadata',
                                        },
                                        fieldGroup: [
                                            {
                                                key: 'account',
                                                type: 'multiInput',
                                                defaultValue: [],
                                                templateOptions: {
                                                    inputOptions: {
                                                        type: 'uiSelect',
                                                        templateOptions: {
                                                            required: true,
                                                            optionsAttr: 'bs-options',
                                                            valueProp: 'value',
                                                            labelProp: 'label',
                                                            groupProp: 'group',
                                                            placeholder: {
                                                                select: 'Select Account Metadata',
                                                                search: 'Search Account Metadata',
                                                            },
                                                            options: [],
                                                        },
                                                    },
                                                },
                                                data: {
                                                    metadataKind: 'account',
                                                    addButtonText: '+ Add Account Metadata',
                                                    immutableFields: [
                                                        {
                                                            name: 'id',
                                                            label: 'Account ID',
                                                        },
                                                    ],
                                                },
                                            },
                                        ],
                                    },
                                    {
                                        wrapper: 'groupedOptions',
                                        className: 'subgrouped-options-wrapper',
                                        templateOptions: {
                                            label: 'Parent Account Metadata',
                                        },
                                        hideExpression: '!$root.subscription.featureFlags.usesSubaccounts',
                                        fieldGroup: [
                                            {
                                                key: 'parentAccount',
                                                type: 'multiInput',
                                                defaultValue: [],
                                                templateOptions: {
                                                    inputOptions: {
                                                        type: 'uiSelect',
                                                        templateOptions: {
                                                            required: true,
                                                            optionsAttr: 'bs-options',
                                                            valueProp: 'value',
                                                            labelProp: 'label',
                                                            groupProp: 'group',
                                                            placeholder: {
                                                                select: 'Select Parent Account Metadata',
                                                                search: 'Search Parent Account Metadata',
                                                            },
                                                            options: [],
                                                        },
                                                    },
                                                },
                                                data: {
                                                    metadataKind: 'parentAccount',
                                                    addButtonText: '+ Add Parent Account Metadata',
                                                    immutableFields: [
                                                        {
                                                            name: 'id',
                                                            label: 'Parent Account ID',
                                                        },
                                                    ],
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                },
                {
                    title: 'Preview',
                    type: 'integration-message-builder',
                },
            ],
        },
        form: {
            groups: [
                {
                    label: 'General',
                    subgroups: [
                        {
                            props: {
                                label: 'Configuration',
                            },
                            fields: [
                                {
                                    type: 'toggle',
                                    defaultValue: false,
                                    path: 'featureFlags.npsMessaging',
                                    props: {
                                        label: 'Enable Notifications',
                                    },
                                },
                                {
                                    type: 'metadata',
                                    path: 'messageSchema.visitor',
                                    defaultValue: [],
                                    props: {
                                        label: 'Visitor Metadata',
                                        metadataType: 'visitor',
                                    },
                                },
                                {
                                    type: 'metadata',
                                    path: 'messageSchema.account',
                                    defaultValue: [],
                                    props: {
                                        label: 'Account Metadata',
                                        metadataType: 'account',
                                    },
                                },
                                {
                                    hideExpression: '!featureFlags.usesSubaccounts',
                                    type: 'metadata',
                                    path: 'messageSchema.parentAccount',
                                    defaultValue: [],
                                    props: {
                                        label: 'Parent Account Metadata',
                                        metadataType: 'parentAccount',
                                    },
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        options: [],
    },
    {
        name: 'slack-unfurl',
        provider: 'Slack',
        label: 'Slack Link Preview',
        categories: ['Collaboration'],
        displayName: 'Slack Link Preview',
        dataDirection: 'Data Out',
        types: ['Native'],
        author: {
            name: 'Pendo',
            email: '<admin>@pendo.io',
            url: 'https://www.pendo.io',
        },
        free: true,
        private: false,
        oauth: {
            uriConfig: {
                category: 'unfurl',
                // entityId will be derived automatically based on the entityType
                // (if Subscription, current sub ID, if Integration, use provider value)
                entityType: ENTITY_TYPE_SUBSCRIPTION,
                provider: 'slack',
            },
        },
        supportsOauth: true,
        isBeta: false,
        oauthType: 'standard',
        tags: ['Collaboration'],
        subText: 'Collaboration just got better with Pendo and Slack together!',
        marketingUrl: 'https://www.pendo.io/integrations/slack/',
        helpUrl: 'https://support.pendo.io/hc/en-us/articles/360058379112-Slack-Integration-Slack-Link-Preview-Beta-',
        description:
            'Paste any Pendo link into Slack  to see a data rich preview of what it’s linking to (unfurl links). Users will see Slack link previews when links to specific analytics or guides are shared. This allows you and your colleagues to benefit from Pendo insights at a glance, without having to jump into the app.',
        media: {
            default: require('@img/integrations/slack.svg'),
            provider: require('@img/integrations/Slack_Logo.svg'),
            thumb: require('@img/integrations/slack.svg'),
            icon: require('@img/integrations/Icon_List_Slack.svg'),
            imagePreview: require('@img/integrations/screenshots/slack-link-preview.gif'),
            screenshots: [],
        },
        /* eslint-disable internal-rules/no-hardcoded-colors */
        brandColor: '#fff',
        /* eslint-enable internal-rules/no-hardcoded-colors */
        options: [],
        imagePreview: {
            images: [
                {
                    src: require('@img/integrations/screenshots/slack-link-preview.gif'),
                },
            ],
            active: true,
        },
        ui: {
            type: 'integration-tabs',
            tabs: [
                {
                    title: 'Preview',
                    type: 'image-preview',
                    images: [
                        {
                            src: require('@img/integrations/screenshots/slack-link-preview.gif'),
                        },
                    ],
                    active: true,
                    form: undefined,
                },
            ],
            bypassForm: true,
            revokeOauth: true,
        },
    },
    {
        name: 'slack-agent',
        provider: 'Slack',
        label: 'Pendo Agent for Slack',
        categories: ['Collaboration'],
        displayName: 'Pendo Agent for Slack',
        dataDirection: 'Two-Way',
        types: ['Native'],
        author: {
            name: 'Pendo',
            email: '<admin>@pendo.io',
            url: 'https://www.pendo.io',
        },
        free: true,
        private: false,
        oauth: {
            uriConfig: {
                category: 'agent',
                entityType: ENTITY_TYPE_SUBSCRIPTION,
                provider: 'slack',
            },
        },
        supportsOauth: true,
        isBeta: true,
        oauthType: 'standard',
        tags: ['Collaboration', 'AI'],
        media: {
            default: require('@img/integrations/slack.svg'),
            provider: require('@img/integrations/Slack_Logo.svg'),
            thumb: require('@img/integrations/slack.svg'),
            icon: require('@img/integrations/Icon_List_Slack.svg'),
            screenshots: [],
        },
        /* eslint-disable internal-rules/no-hardcoded-colors */
        brandColor: '#FFFFFF',
        /* eslint-enable internal-rules/no-hardcoded-colors */
        description:
            'Ask questions about your product data directly from Slack. Get answers and summaries, and take action without leaving your conversation.',
        marketingUrl: 'https://www.pendo.io/integrations/slack/',
        helpUrl: 'https://support.pendo.io/hc/en-us/articles/47381856967195',
        options: [],
        hideEditButton: true,
        ui: {
            type: 'integration-tabs',
            tabs: [
                {
                    title: 'Preview',
                    type: 'image-preview',
                    images: [],
                    active: true,
                    form: undefined,
                },
            ],
            bypassForm: true,
            revokeOauth: true,
        },
        requirements: ['Leo'],
        requiredFlags: ['askPendo'],
    },
    {
        name: 'slack-voc',
        provider: 'Slack',
        label: $t('slackMessagesSetup.catalog_label'),
        categories: ['Collaboration', 'Feedback'],
        displayName: $t('slackMessagesSetup.catalog_label'),
        dataDirection: 'Data In',
        types: ['Native'],
        author: {
            name: 'Pendo',
            email: '<admin>@pendo.io',
            url: 'https://www.pendo.io',
        },
        free: false,
        private: false,
        oauth: {
            uriConfig: {
                category: 'voc',
                entityType: ENTITY_TYPE_SUBSCRIPTION,
                provider: 'slack',
            },
        },
        supportsOauth: true,
        isBeta: true,
        oauthType: 'standard',
        tags: ['Collaboration', 'Feedback'],
        media: {
            default: require('@img/integrations/slack.svg'),
            provider: require('@img/integrations/Slack_Logo.svg'),
            thumb: require('@img/integrations/slack.svg'),
            icon: require('@img/integrations/Icon_List_Slack.svg'),
            screenshots: [],
        },
        /* eslint-disable internal-rules/no-hardcoded-colors */
        brandColor: '#FFFFFF',
        /* eslint-enable internal-rules/no-hardcoded-colors */
        marketingUrl: 'https://www.pendo.io/integrations/slack/',
        description: $t('slackMessagesSetup.catalog_description'),
        options: [],
        workflowType: 'voc',
        workflow: 'SlackMessages',
        ui: {
            bypassForm: true,
            revokeOauth: true,
        },
        requirements: ['Listen'],
    },
    {
        name: 'trustRadius',
        provider: 'TrustRadius',
        label: 'TrustRadius',
        categories: ['Feedback'],
        displayName: 'TrustRadius',
        dataDirection: null,
        types: ['Guide Embedded'],
        author: {
            name: 'Pendo',
            email: '<admin>@pendo.io',
            url: 'https://www.pendo.io',
        },
        private: true,
        allowedSubs: [PENDO_INTERNAL_SUB],
        tags: ['NPS'],
        media: {
            default: require('@img/integrations/trustradius.svg'),
            provider: require('@img/integrations/trustradius_logo.svg'),
            thumb: require('@img/integrations/trustradius_thumbnail.svg'),
            icon: require('@img/integrations/Icon_List_TrustRadius.svg'),
            screenshots: [],
        },
        /* eslint-disable internal-rules/no-hardcoded-colors */
        brandColor: '#022873',
        /* eslint-enable internal-rules/no-hardcoded-colors */
        description:
            'Embed TrustRadius review requests into targeted Pendo guides to gather real-world product feedback and public social proof for in-market buyers.',
        marketingUrl: 'https://www.pendo.io/integrations/trustradius/',
        helpUrl:
            'https://support.pendo.io/hc/en-us/articles/360032986852-TrustRadius-Recipe-Drive-Reviews-and-Customer-Proof',
        form: {
            groups: [
                {
                    label: 'General',
                    subgroups: [
                        {
                            fields: [
                                {
                                    type: 'text',
                                    path: 'url',
                                    props: {
                                        label: 'TrustRadius url',
                                        placeholder: 'https://www.trustradius.com/products/acme/reviews',
                                        required: true,
                                    },
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        options: [
            {
                key: 'url',
                type: 'input',
                defaultValue: '',
                templateOptions: {
                    placeholder: 'https://www.trustradius.com/products/acme/reviews',
                    label: 'TrustRadius url',
                    group: 'general',
                    required: true,
                },
            },
        ],
    },
];

// Deprecated: use getIntegrationCatalog() for i18n support.
export const integrationCatalog = getIntegrationCatalog();
